import { QuoteType, UnitActionMode } from "@/types/enums";
import type { Facility, UnitGroup, UnitTableOptions } from "@/types";

export interface UnitActionAvailability {
    /** Unit type is flagged click-to-call in SE (ReserveOnlyUnitTypes). */
    isClickToCall: boolean;
    /** Rent Online is offered for this group. */
    canRent: boolean;
    /** Reserve is offered for this group. */
    canReserve: boolean;
}

type DisableOptions = Pick<
    UnitTableOptions,
    "disableRental" | "disableReservation" | "disableSoftReservation"
>;

/**
 * Pure derivation of whether a unit group can be rented / reserved online.
 * Single source of truth shared by useTriggerButtonVisibility (which button to
 * render) and the market-mode visibility filter (whether to render the row at
 * all) so the two can never drift apart.
 *
 * Mirrors useTriggerButtonVisibility exactly:
 * - canRent    = rentals not disabled AND not reserve-only AND not click-to-call
 * - canReserve = reservations not disabled AND not click-to-call
 *                AND (reservation window allows OR reserve-only overrides it)
 */
export const getUnitActionAvailability = (
    unitGroup: UnitGroup,
    options: Pick<UnitTableOptions, "disableRental" | "disableReservation">,
): UnitActionAvailability => {
    const reservationType = unitGroup.representative_unit.reservation_type;
    const isClickToCall = reservationType === UnitActionMode.CLICK_TO_CALL;
    const isReserveOnly = reservationType === UnitActionMode.RESERVE_ONLY;

    const canRent = !options.disableRental && !isReserveOnly && !isClickToCall;
    const canReserve =
        !options.disableReservation &&
        !isClickToCall &&
        (unitGroup.reservation_allowed || isReserveOnly);

    return { isClickToCall, canRent, canReserve };
};

/**
 * True when a unit group resolves to the phone-only "Call for Availability"
 * button — i.e. it can neither be rented nor reserved online AND it does not
 * fall back to a "Get a Quote" (soft-reservation) lead form.
 *
 * Mirrors TriggerButtons' resolution order:
 * - click-to-call always calls (regardless of quote_type);
 * - otherwise a still-transactable group is NOT a call;
 * - a non-transactable group calls when soft reservations are disabled OR the
 *   facility's quote_type is anything but "reserve" (SE default is "call").
 *   quote_type "reserve" yields Get a Quote, which is a lead path, not a call.
 */
export const resolvesToCallForAvailability = (
    unitGroup: UnitGroup,
    facility: Facility | undefined,
    options: DisableOptions,
): boolean => {
    const { isClickToCall, canRent, canReserve } = getUnitActionAvailability(
        unitGroup,
        options,
    );

    if (isClickToCall) return true;
    if (canRent || canReserve) return false;
    if (options.disableSoftReservation) return true;

    return facility?.settings?.quote_type !== QuoteType.RESERVE;
};
